import { useState } from "react";
import {
  DeviceType,
  selectLocalAudioTrackID,
  selectTrackAudioByID,
  useDevices,
  useHMSStore,
} from "@100mslive/react-sdk";
import { Mic, Video, Volume2 } from "lucide-react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

const TEST_SOUND = "/test-audio.wav";

const canChooseSpeaker = () =>
  typeof HTMLMediaElement !== "undefined" &&
  "setSinkId" in HTMLMediaElement.prototype;

/** How loud your microphone is right now: talk and watch it move. */
const MicLevel = () => {
  const trackId = useHMSStore(selectLocalAudioTrackID);
  const level = useHMSStore(selectTrackAudioByID(trackId)) || 0;
  return (
    <span
      className="flex h-3 items-end gap-0.5"
      role="meter"
      aria-label="Microphone level"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(level)}
      data-testid="mic_level"
    >
      {[10, 25, 45, 65, 85].map(threshold => (
        <span
          key={threshold}
          className={cn(
            "w-1 rounded-full transition-colors",
            level >= threshold ? "bg-emerald-500" : "bg-border"
          )}
          style={{ height: `${4 + threshold / 12}px` }}
        />
      ))}
    </span>
  );
};

const SpeakerTest = ({ deviceId }) => {
  const [playing, setPlaying] = useState(false);

  const play = async () => {
    const audio = new Audio(TEST_SOUND);
    try {
      if (deviceId && audio.setSinkId) {
        await audio.setSinkId(deviceId);
      }
      setPlaying(true);
      audio.onended = () => setPlaying(false);
      await audio.play();
    } catch (error) {
      setPlaying(false);
    }
  };

  return (
    <button
      type="button"
      onClick={play}
      disabled={playing}
      data-testid="speaker_test_btn"
      className="text-xs font-medium text-brand underline-offset-4 hover:underline disabled:text-muted-foreground disabled:no-underline"
    >
      {playing ? "Playing…" : "Play test sound"}
    </button>
  );
};

const DeviceSelect = ({
  id,
  label,
  icon: Icon,
  devices,
  selected,
  onChange,
  aside,
}) => (
  <div className="grid min-w-0 gap-1.5">
    <div className="flex items-center justify-between gap-2">
      <Label htmlFor={id} className="gap-1.5 text-xs text-muted-foreground">
        <Icon className="size-3.5" />
        {label}
      </Label>
      {aside}
    </div>
    <Select value={selected || ""} onValueChange={onChange}>
      {/* Device names can be long ("Default - INZONE H9 / INZONE H7 - Game
          (054c:0e53)"): truncate them instead of widening the dialog. */}
      <SelectTrigger
        id={id}
        className="w-full min-w-0 *:data-[slot=select-value]:block *:data-[slot=select-value]:min-w-0 *:data-[slot=select-value]:truncate"
        data-testid={`${id}_select`}
      >
        <SelectValue placeholder="Default" />
      </SelectTrigger>
      <SelectContent>
        {devices.map((device, index) => (
          <SelectItem key={device.deviceId} value={device.deviceId}>
            {device.label || `${label} ${index + 1}`}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

/**
 * Camera, microphone (with a level meter) and speaker (with a test sound),
 * after Cal.com's pre-join card. Used by the preview and the settings dialog.
 */
export const DevicePickers = ({ className }) => {
  const { allDevices, selectedDeviceIDs, updateDevice } = useDevices();
  // Before permission is granted browsers list devices without an id, which
  // the SDK can't open: those are left out rather than offered.
  const usable = devices => (devices || []).filter(device => device.deviceId);
  const videoInput = usable(allDevices?.videoInput);
  const audioInput = usable(allDevices?.audioInput);
  const audioOutput = usable(allDevices?.audioOutput);

  const change = deviceType => deviceId =>
    updateDevice({ deviceType, deviceId });

  return (
    <div className={cn("grid grid-cols-1 gap-3", className)}>
      {videoInput.length > 0 ? (
        <DeviceSelect
          id="camera"
          label="Camera"
          icon={Video}
          devices={videoInput}
          selected={selectedDeviceIDs?.videoInput}
          onChange={change(DeviceType.videoInput)}
        />
      ) : null}
      {audioInput.length > 0 ? (
        <DeviceSelect
          id="microphone"
          label="Microphone"
          icon={Mic}
          devices={audioInput}
          selected={selectedDeviceIDs?.audioInput}
          onChange={change(DeviceType.audioInput)}
          aside={<MicLevel />}
        />
      ) : null}
      {audioOutput.length > 0 && canChooseSpeaker() ? (
        <DeviceSelect
          id="speaker"
          label="Speaker"
          icon={Volume2}
          devices={audioOutput}
          selected={selectedDeviceIDs?.audioOutput}
          onChange={change(DeviceType.audioOutput)}
          aside={<SpeakerTest deviceId={selectedDeviceIDs?.audioOutput} />}
        />
      ) : null}
    </div>
  );
};
